import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2, FileText, CheckCircle2, XCircle, AlertTriangle, Clock, Info, Sparkles, Send } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

export default function Logs() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncTypeFilter, setSyncTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // A2 Log Assistant chat state
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]); // [{role: 'user'|'assistant', text, sources?, tools?}]
  const [asking, setAsking] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (syncTypeFilter !== 'all') params.sync_type = syncTypeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await api.getLogs(token, params);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, syncTypeFilter, statusFilter, currentLocationId]);

  const askAssistant = async (overrideQuestion) => {
    const q = (overrideQuestion || question).trim();
    if (!q || asking) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setQuestion('');
    setAsking(true);

    // Placeholder assistant message we'll stream into
    setMessages((m) => [...m, { role: 'assistant', text: '', tools: [], streaming: true }]);
    const idx = -1;
    let accumulated = '';
    const usedTools = [];

    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || '')}/api/agents/logs/ask-stream`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ question: q }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event: '));
          const dataLine = lines.find((l) => l.startsWith('data: '));
          if (!eventLine || !dataLine) continue;
          const ev = eventLine.slice(7).trim();
          let payload;
          try {
            payload = JSON.parse(dataLine.slice(6));
          } catch {
            continue;
          }
          if (ev === 'text' && payload.chunk) {
            accumulated += payload.chunk;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length + idx] = {
                ...copy[copy.length + idx],
                text: accumulated,
              };
              return copy;
            });
          } else if (ev === 'tool' && payload.name) {
            usedTools.push(payload.name);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length + idx] = { ...copy[copy.length + idx], tools: [...usedTools] };
              return copy;
            });
          } else if (ev === 'done') {
            // Split out sources line if present
            const parts = accumulated.split(/\n\s*Sources:\s*/i);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length + idx] = {
                ...copy[copy.length + idx],
                text: parts[0].trim(),
                sources: parts[1] ? parts[1].trim() : null,
                streaming: false,
              };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length + idx] = {
          role: 'assistant',
          text: 'Sorry — the assistant is unavailable right now.',
          error: true,
        };
        return copy;
      });
    } finally {
      setAsking(false);
    }
  };

  // Deep link: /logs?ask=... auto-submits so alert-page "Ask" buttons work
  useEffect(() => {
    const prefilled = searchParams.get('ask');
    if (prefilled) {
      askAssistant(prefilled);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-rose-500" />;
      case 'partial': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default: return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'status-success',
      failed: 'status-error',
      partial: 'status-pending',
      pending: 'status-synced'
    };
    return styles[status] || 'status-synced';
  };

  const getLogLevelColor = (status) => {
    switch (status) {
      case 'success': return 'text-emerald-400';
      case 'failed': return 'text-rose-400';
      case 'partial': return 'text-amber-400';
      default: return 'text-blue-400';
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const successCount = logs.filter(l => l.status === 'success').length;
  const failedCount = logs.filter(l => l.status === 'failed').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Sync Logs
          </h1>
          <p className="text-slate-400 mt-1">Detailed history of all sync operations</p>
        </div>
      </div>

      {/* A2 — Log Assistant */}
      <Card
        className="bg-gradient-to-br from-purple-500/[0.04] via-slate-900/60 to-slate-900/40 border-purple-500/20"
        data-testid="log-assistant-card"
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-300" />
            Ask the Log Assistant
            <span className="ml-2 text-[10px] uppercase tracking-wider text-purple-300/70 border border-purple-500/30 rounded px-1.5 py-0.5">
              beta · read-only
            </span>
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Plain-English questions about your sync activity. Every answer cites its sources.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {messages.length > 0 && (
            <div
              className="rounded-lg border border-slate-800 bg-slate-950/40 max-h-[280px] overflow-y-auto p-3 space-y-3 text-sm"
              data-testid="log-assistant-transcript"
            >
              {messages.map((m, i) => (
                <div key={i} className={m.role === 'user' ? 'text-slate-300' : 'text-slate-100'}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    {m.role === 'user' ? 'You' : 'Assistant'}
                  </div>
                  <div className={m.error ? 'text-rose-300' : ''}>{m.text}</div>
                  {m.role === 'assistant' && m.tools?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {m.tools.map((t, ti) => (
                        <span
                          key={ti}
                          className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.sources && (
                    <div className="mt-1.5 text-xs text-slate-500 font-mono">
                      Sources: {m.sources}
                    </div>
                  )}
                </div>
              ))}
              {asking && (
                <div className="text-slate-500 text-xs flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Investigating…
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  askAssistant();
                }
              }}
              placeholder="e.g. Why is there an inventory alert badge? · How did today's syncs go? · Any failures this week?"
              className="bg-slate-900/60 border-slate-800 text-slate-100 min-h-[52px] resize-none"
              data-testid="log-assistant-input"
            />
            <Button
              onClick={askAssistant}
              disabled={asking || !question.trim()}
              className="bg-purple-500 hover:bg-purple-600 text-white h-[52px]"
              data-testid="log-assistant-submit"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {[
                "How did today's syncs go?",
                'Any failures this week?',
                'What inventory alerts are open?',
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setQuestion(s)}
                  className="text-xs px-2 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
                  data-testid={`log-assistant-suggest-${s.slice(0, 12)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{logs.length}</p>
            <p className="text-slate-400 text-sm">Total Logs</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{successCount}</p>
            <p className="text-slate-400 text-sm">Successful</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-rose-400">{failedCount}</p>
            <p className="text-slate-400 text-sm">Failed</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">
              {logs.filter(l => l.status === 'partial').length}
            </p>
            <p className="text-slate-400 text-sm">Partial</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={syncTypeFilter} onValueChange={setSyncTypeFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="sync-type-filter">
                <SelectValue placeholder="Sync Type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="products">Products</SelectItem>
                <SelectItem value="customers">Customers</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="inventory">Inventory</SelectItem>
                <SelectItem value="accounting">Accounting</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No logs found. Perform sync operations to generate logs.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400 w-10"></TableHead>
                  <TableHead className="text-slate-400">Timestamp</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Direction</TableHead>
                  <TableHead className="text-slate-400">Records</TableHead>
                  <TableHead className="text-slate-400">Duration</TableHead>
                  <TableHead className="text-slate-400">Message</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow 
                    key={log.id} 
                    className={cn(
                      "border-slate-800 data-table-row",
                      log.status === 'failed' && "bg-rose-500/5"
                    )}
                  >
                    <TableCell>{getStatusIcon(log.status)}</TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-700 text-slate-300 capitalize">
                        {log.sync_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {log.source} → {log.destination}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400 font-mono">{log.records_processed}</span>
                        {log.records_failed > 0 && (
                          <span className="text-rose-400 font-mono">/ {log.records_failed} failed</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">
                      {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                    </TableCell>
                    <TableCell className={cn("text-sm max-w-[200px] truncate", getLogLevelColor(log.status))}>
                      {log.message}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(log.status)}>{log.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
